import type { Community } from '@communities/community/domain';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { CommunityAuditAccuracyChart } from '@internal-audit/components/CommunityAuditAccuracyChart';
import { CommunityAuditHeroImage } from '@internal-audit/components/CommunityAuditHeroImage';
import { CommunityAuditLabelAnalysis } from '@internal-audit/components/CommunityAuditLabelAnalysis';
import { CommunityAuditPerformanceMetrics } from '@internal-audit/components/CommunityAuditPerformanceMetrics';
import { CommunityAuditProductionStatus } from '@internal-audit/components/CommunityAuditProductionStatus';
import type { AuditReport } from '@internal-audit/domain';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { useGetAuditReportsByDateRange } from '@internal-audit/hooks/useGetAuditReportsByDateRange';
import { useGetComprehensiveAnalytics } from '@internal-audit/hooks/useGetComprehensiveAnalytics';
import { Heading } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import * as styles from './CommunityAuditHeaderSection.css';

interface CommunityAuditHeaderSectionProps {
  community: Community | null;
  auditSessions: FlexibleAuditSession[];
  auditReports?: AuditReport[];
  isLoading?: boolean;
}

/**
 * Header section component for audit pages with gradient transition
 * Contains community name, image, and accuracy over time graph
 * Background image is handled by the global useHeroBackground hook
 */
// eslint-disable-next-line complexity
function CommunityAuditHeaderSectionComponent({ community, auditSessions, auditReports, isLoading = false }: CommunityAuditHeaderSectionProps) {
  const name = community?.name || '';

  // Calculate date range for the last 1 year to get meaningful chart data
  const dateRange = useMemo(() => {
    const toDate = new Date();
    const fromDate = new Date();

    fromDate.setFullYear(fromDate.getFullYear() - 1); // Last 1 year

    return {
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString(),
    };
  }, []);

  // Fetch audit reports by date range for the chart
  const { auditReports: auditReportsByDateRange, loading: auditReportsLoading } = useGetAuditReportsByDateRange({
    communityId: community?.id,
    fromDate: dateRange.fromDate,
    toDate: dateRange.toDate,
    options: {
      skip: !community?.id,
    },
  });

  // Fetch comprehensive analytics for the same time period
  const { comprehensiveAnalytics, isLoading: analyticsIsLoading } = useGetComprehensiveAnalytics({
    communityId: community?.id || '',
    fromDate: dateRange.fromDate,
    toDate: dateRange.toDate,
    skip: !community?.id,
  });

  // Use the fetched audit reports for the chart, fallback to prop if provided
  const chartAuditReports = auditReportsByDateRange.length > 0 ? auditReportsByDateRange : auditReports || [];

  // Coordinate loading states - all panels should load together
  const anyDataLoading = isLoading || auditReportsLoading || analyticsIsLoading;
  const chartIsLoading = anyDataLoading;
  const analyticsComponentsLoading = anyDataLoading;

  return (
    <div className={styles.heroSection}>
      {/* Back Button Placeholder - Invisible but takes up same space as detail pages for consistent positioning */}
      <div className={styles.backButtonPlaceholder} />

      {/* Gradient Overlay */}
      <div className={styles.heroOverlay} />

      <div className={styles.heroContent}>
        {/* Left Column - Image */}
        <CommunityAuditHeroImage community={community} isLoading={isLoading} />

        {/* Right Column - Content */}
        <div className={styles.heroContentRight}>
          {/* Community Title */}
          <Heading className={styles.communityTitle} level="h1">
            {name} Audits
          </Heading>

          {/* 4-Panel Grid Layout */}
          <div className={styles.fourPanelContainer}>
            <div className={styles.fourPanelGrid}>
              {/* Performance Metrics Over Time Chart Panel */}
              <CommunityAuditAccuracyChart auditReports={chartAuditReports} auditSessions={auditSessions} isLoading={chartIsLoading} />

              {/* Performance Metrics Bar Chart Panel */}
              <CommunityAuditPerformanceMetrics comprehensiveAnalytics={comprehensiveAnalytics} isLoading={analyticsComponentsLoading} />

              {/* Label Analysis Panel */}
              <CommunityAuditLabelAnalysis comprehensiveAnalytics={comprehensiveAnalytics} isLoading={analyticsComponentsLoading} />

              {/* Audit History Panel - Now Last */}
              <CommunityAuditProductionStatus
                auditSessions={auditSessions}
                comprehensiveAnalytics={comprehensiveAnalytics}
                isLoading={analyticsComponentsLoading}
              />

              {/* Consolidated Footer for Analytics Panels */}
              <div className={styles.analyticsFooter}>
                <span className={styles.analyticsFooterText}>Averaged across all completed audit reports (max 100)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const CommunityAuditHeaderSection = withAppInsightsTracking(CommunityAuditHeaderSectionComponent, 'CommunityAuditHeaderSection');
